import type { SetupLaterStep, SetupState, SetupStepId } from '@/types';
import type { useBillingNouns } from '@/hooks/useSession';

export type Nouns = ReturnType<typeof useBillingNouns>;

export interface StepCta {
  label: string;
  /** Where the CTA goes. Absent for a step whose action is inside the rail (the "how" card). */
  to?: string;
  /** The label once the person is already on the step's page; the button then points at the target instead of navigating. */
  here?: string;
}

/** Scroll the step's target into view and put focus on its first control. */
export function focusTourTarget(target: string | null | undefined): void {
  if (!target) return;
  const el = document.querySelector<HTMLElement>(`[data-tour="${target}"]`);
  if (!el) return;
  el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  const control = el.matches('input, select, textarea, button, [role="combobox"]')
    ? el
    : el.querySelector<HTMLElement>('input, select, textarea, button, [role="combobox"]');
  (control ?? el).focus({ preventScroll: true });
}

export interface StepConfig {
  id: SetupStepId;
  /** The page the step happens on; the rail's in-place CTA focuses `target` there. */
  route: string;
  /** The `data-tour` value of the element to ring, or null for a page with no single target. */
  target: string | null;
  title: string;
  /** Two lines at most. */
  body: string;
  cta: StepCta;
  isDone(state: SetupState): boolean;
}

export interface LaterConfig {
  id: SetupLaterStep['id'];
  title: string;
  body: string;
  to: string;
}

/** The `data-tour` targets the shell must put on real elements. */
export const TOUR_TARGETS = {
  settingsCompany: 'settings-company',
  clientsAdd: 'clients-add',
  activitiesSuggested: 'activities-suggested',
  timesheetEntryRow: 'timesheet-entry-row',
  invoicePreview: 'invoice-preview'
} as const;

function doneFromState(id: SetupStepId) {
  return (state: SetupState) => state.steps.find((s) => s.id === id)?.done ?? false;
}

/**
 * The step list in the team's own nouns. Members (not admins) get the three
 * steps that are theirs; the firm's setup stays with the admin.
 */
export function buildSteps(nouns: Nouns, isAdmin: boolean): StepConfig[] {
  const direct = nouns.client === 'Project';
  const client = nouns.clientLower;
  const payer = nouns.payer.toLowerCase();

  const how: StepConfig = {
    id: 'how',
    route: '/timesheet',
    target: null,
    title: 'How Billable works',
    body: direct
      ? 'Projects, billing contacts, time and invoices. One minute.'
      : 'Clients, billing parties, time and invoices. One minute.',
    cta: { label: 'Next: your firm' },
    isDone: doneFromState('how')
  };

  const firm: StepConfig = {
    id: 'firm',
    route: '/settings',
    target: TOUR_TARGETS.settingsCompany,
    title: 'Your firm',
    body: 'Name, address, how you bill, default rate and terms.',
    cta: { label: 'Open settings', to: '/settings', here: 'Done' },
    isDone: doneFromState('firm')
  };

  const importStep: StepConfig = {
    id: 'import',
    route: '/manage/import',
    target: null,
    title: 'Bring in existing work',
    body: 'Sheets, CSV, Harvest, Toggl, Clockify and more. Or start fresh.',
    cta: { label: 'Choose a source', to: '/manage/import' },
    isDone: doneFromState('import')
  };

  const clientStep: StepConfig = {
    id: 'client',
    route: '/manage/clients',
    target: TOUR_TARGETS.clientsAdd,
    title: `Add a ${client}`,
    body: direct
      ? `A project is the engagement. Pick the ${payer} from its dropdown.`
      : `A client is the case, one per matter. Pick the ${payer} from its dropdown.`,
    cta: { label: `Add a ${client}`, to: '/manage/clients', here: `Add a ${client}` },
    isDone: doneFromState('client')
  };

  const activities: StepConfig = {
    id: 'activities',
    route: '/manage/activities',
    target: TOUR_TARGETS.activitiesSuggested,
    title: 'Your activities',
    body: 'Type your own, or start from a set and untick what you do not need.',
    cta: { label: 'Choose activities', to: '/manage/activities', here: 'Choose activities' },
    isDone: doneFromState('activities')
  };

  const entry: StepConfig = {
    id: 'entry',
    route: '/timesheet',
    target: TOUR_TARGETS.timesheetEntryRow,
    title: 'Log time',
    body: `Pick a ${client}, an activity and the hours. The row is ready.`,
    cta: { label: 'Log time', to: '/timesheet', here: 'Add your first entry' },
    isDone: doneFromState('entry')
  };

  const invoice: StepConfig = {
    id: 'invoice',
    route: '/invoices/new',
    target: TOUR_TARGETS.invoicePreview,
    title: 'Send an invoice',
    body: direct
      ? `A project's unbilled time, sent to the ${payer}. A saved draft counts.`
      : `A client's unbilled time, sent to the ${payer}. A saved draft counts.`,
    cta: { label: 'New invoice', to: '/invoices/new', here: 'Pick the time to bill' },
    isDone: doneFromState('invoice')
  };

  return isAdmin
    ? [how, firm, importStep, clientStep, activities, entry, invoice]
    : [how, entry, invoice];
}

/** "When you're ready": admins only, never blocking. */
export const LATER_STEPS: LaterConfig[] = [
  {
    id: 'payments',
    title: 'Get paid online',
    body: 'Connect Stripe so every invoice carries a pay link.',
    // Online payments live on invoice settings (its first card), not /settings.
    to: '/invoice-settings'
  },
  {
    id: 'team',
    title: 'Invite your team',
    body: 'They log their own time. You still own the setup.',
    to: '/team'
  }
];

export function doneCount(steps: StepConfig[], state: SetupState): number {
  return steps.filter((s) => s.isDone(state)).length;
}

export function firstUndone(steps: StepConfig[], state: SetupState): StepConfig | undefined {
  return steps.find((s) => !s.isDone(state));
}

export function stepSource(state: SetupState, id: SetupStepId) {
  return state.steps.find((s) => s.id === id)?.source;
}

export function laterDone(state: SetupState, id: LaterConfig['id']): boolean {
  return state.later.find((l) => l.id === id)?.done ?? false;
}

const COUNT_LABELS: Record<string, [string, string]> = {
  client: ['client', 'clients'],
  clients: ['client', 'clients'],
  billable: ['billing party', 'billing parties'],
  billables: ['billing party', 'billing parties'],
  activity: ['activity', 'activities'],
  activities: ['activity', 'activities'],
  entry: ['entry', 'entries'],
  entries: ['entry', 'entries'],
  invoice: ['invoice', 'invoices'],
  invoices: ['invoice', 'invoices'],
  fee: ['fee', 'fees'],
  fees: ['fee', 'fees']
};

/** "12 clients, 410 entries" from an import's counts map. Zero counts are left out. */
export function describeCounts(counts: Record<string, number>): string {
  const parts: string[] = [];
  for (const [key, n] of Object.entries(counts)) {
    if (!n) continue;
    const labels = COUNT_LABELS[key] ?? [key, key];
    parts.push(`${n} ${n === 1 ? labels[0] : labels[1]}`);
  }
  return parts.join(', ');
}
